import CONFIG from '../config/config';
import { instanceWithV2 as APIClient } from './SimpleAPIClient';

export type StatisticsApiResponse = {
  [collectionName: string]: { [modelName: string]: number };
};

export type StatisticsFilters = {
  startTime?: string;
  endTime?: string;
  shopId?: string;
  partnerId?: string;
};

export type CustomerVisit = {
  appVersion: string,
  collectionId: number,
  name: string,
  createdDate: number,
  dateOfWedding: string,
  dressesName: string,
  id: number,
  region: string,
  shopId: string,
  surname: string
}

type VisitorListApiResponse = CustomerVisit[]

export type OverviewApiResponse = {
  previousMonth: {
    orderCount: number;
    profit: number;
  };
  currentMonth: {
    orderCount: number;
    profit: number;
  };
  difference: {
    orderCount: number;
    profit: number;
  };
  currency: string;
};

const StatisticsAPI = {
  getMostPopularDresses: () =>
    APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(
      '/v1/statistics/customers/summary/mostPopularDresses'
    ),
  getWeddingMonthSummary: () =>
    APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(
      '/v1/statistics/customers/summary/weddingMonth'
  ),
  getByShops: () =>
    APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(
    '/v1/statistics/customers/summary/total'
  ),
  getVisitors: () =>
    APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(
    '/v1/statistics/customers/summary/noOfVisitors'
  ),
  getVisitorsList: (startTime: string, endTime: string) =>
    APIClient(CONFIG.API_ENDPOINT).get<VisitorListApiResponse>(
    `/v1/customers?startTime=${startTime}&endTime=${endTime}`
  ),
  deleteVisit: (visitId: number) =>
    APIClient(CONFIG.API_ENDPOINT).delete<VisitorListApiResponse>(
    `/v1/statistics?id=${visitId}`
  ),
  getTotalOrdersPerYear: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    
    const queryString = params.toString();
    const url = `/v1/statistics/orders/summary/total-per-year${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getMonthlyOrdersGroupedByYear: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    
    const queryString = params.toString();
    const url = `/v1/statistics/orders/summary/monthly-grouped-by-year${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getMonthlyOrdersByShopGroupedByYear: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    
    const queryString = params.toString();
    const url = `/v1/statistics/orders/summary/monthly-by-shop-grouped-by-year${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getTotalSalesByShopGroupedByYear: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    
    const queryString = params.toString();
    const url = `/v1/statistics/orders/summary/total-sales-by-shop-grouped-by-year${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getActiveOrdersGroupedByStatus: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    
    const queryString = params.toString();
    const url = `/v1/statistics/orders/summary/active-orders-grouped-by-status${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getTotalSalesByDressGroupedByYear: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    if (filters?.shopId) params.append('shopId', filters.shopId);
    if (filters?.partnerId) params.append('partnerId', filters.partnerId);
    
    const queryString = params.toString();
    const url = `/v1/statistics/orders/summary/sold-dresses-per-year${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getOverview: () =>
    APIClient(CONFIG.API_ENDPOINT).get<OverviewApiResponse>(
      '/v1/statistics/orders/summary/overview'
  ),
  // eCommerce Statistics
  getTopSellingItems: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    
    const queryString = params.toString();
    const url = `/v1/statistics/store/top-selling-items${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getIncomeOverTime: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    
    const queryString = params.toString();
    const url = `/v1/statistics/store/income-over-time${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getTopBookmarkedItems: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    
    const queryString = params.toString();
    const url = `/v1/statistics/store/top-bookmarked-items${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getTopViewedItems: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    
    const queryString = params.toString();
    const url = `/v1/statistics/store/top-viewed-items${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
  getVisitorSources: (filters?: StatisticsFilters) => {
    const params = new URLSearchParams();
    if (filters?.startTime) params.append('startTime', filters.startTime);
    if (filters?.endTime) params.append('endTime', filters.endTime);
    
    const queryString = params.toString();
    const url = `/v1/statistics/store/visitor-sources${queryString ? `?${queryString}` : ''}`;
    
    return APIClient(CONFIG.API_ENDPOINT).get<StatisticsApiResponse>(url);
  },
};

export default StatisticsAPI;
